import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingDown,
  Target,
  TrendingUp,
  Trash2,
} from "lucide-react";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import WarningPopup from "../WarningPopup/WarningPopup";

// Helper function to check if leave is full day
const isFullDayLeave = (leaveDuration) => {
  if (!leaveDuration) return false;
  const duration = leaveDuration.trim().toUpperCase();
  return duration === "FULL_DAY" || duration === "FULL DAY";
};

// Helper functions to interpret leave approval status
const normalizeLeaveStatus = (leaveStatus) =>
  (leaveStatus || "").trim().toUpperCase();

const isPendingLeave = (leaveStatus) => {
  const status = normalizeLeaveStatus(leaveStatus);
  return status === "REQUESTED" || status === "PENDING";
};

const isRevokedLeave = (leaveStatus) => {
  const status = normalizeLeaveStatus(leaveStatus);
  return (
    status === "REVOKED" || status === "CANCELLED" || status === "CANCELED"
  );
};

// A revoked leave no longer blocks the day — the employee is treated as
// working that day, so it should never drive attendance/leave counts.
const getEffectiveLeave = (leave) =>
  leave && !isRevokedLeave(leave.leaveStatus) ? leave : null;

// Day Details Modal with Holiday Information
const DayDetailsModal = ({
  record,
  dayInfo,
  workingDays,
  onClose,
  canDelete = false,
  onDelete,
}) => {
  if (!record && !dayInfo) return null;

  const formatTime = (timeString) => {
    if (!timeString || timeString === "—") return "—";
    return timeString;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getHolidayReason = (date) => {
    if (dayInfo?.isCompanyHoliday) {
      return `${dayInfo.holidayName} - Company Holiday (${dayInfo.holidayType || "PUBLIC"})`;
    }

    if (dayInfo?.dayType !== "holiday" || !workingDays) return null;

    const currentDate = new Date(date);
    const dayOfWeek = currentDate.getDay();
    const weekOfMonth = Math.ceil(currentDate.getDate() / 7);
    const weekNames = ["first", "second", "third", "fourth", "fifth"];
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const weekName = weekNames[weekOfMonth - 1];
    const dayName = dayNames[dayOfWeek];

    if (!workingDays[dayName]) {
      return `${
        dayNames[dayOfWeek].charAt(0).toUpperCase() +
        dayNames[dayOfWeek].slice(1)
      } - Weekly Off`;
    }

    if (
      dayName === "saturday" &&
      workingDays[
        `leaveOn${weekName.charAt(0).toUpperCase() + weekName.slice(1)}Saturday`
      ]
    ) {
      return `${
        weekName.charAt(0).toUpperCase() + weekName.slice(1)
      } Saturday - Weekly Off`;
    }

    if (
      dayName === "sunday" &&
      workingDays[
        `leaveOn${weekName.charAt(0).toUpperCase() + weekName.slice(1)}Sunday`
      ]
    ) {
      return `${
        weekName.charAt(0).toUpperCase() + weekName.slice(1)
      } Sunday - Weekly Off`;
    }

    return "Holiday";
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Attendance Details</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {dayInfo?.employeeName && (
            <div className="detail-row">
              <span className="detail-label">Employee</span>
              <span className="detail-value">{dayInfo?.employeeName}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Date</span>
            <span className="detail-value">{formatDate(dayInfo?.date)}</span>
          </div>

          {(dayInfo?.dayType || dayInfo?.isCompanyHoliday) && (
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span
                className={`badge ${
                  dayInfo?.isCompanyHoliday
                    ? "badge-company-holiday"
                    : `badge-${dayInfo.dayType}`
                }`}
              >
                {dayInfo?.isCompanyHoliday
                  ? `🎉 ${dayInfo.holidayName}`
                  : dayInfo.dayType === "holiday"
                    ? "Holiday"
                    : dayInfo.dayType === "leave-requested"
                      ? `Leave Requested (${dayInfo.leaveType || "N/A"})`
                      : dayInfo.dayType === "leave"
                        ? `Leave (${dayInfo.leaveType || "N/A"})`
                        : "Working Day"}
              </span>
            </div>
          )}

          {dayInfo?.isJoiningDay && (
            <div className="detail-row">
              <span className="detail-label">Milestone</span>
              <span className="badge badge-joined">🎉 Date of Joining</span>
            </div>
          )}

          {dayInfo?.leaveRevoked && (
            <div className="detail-row">
              <span className="detail-label">Leave</span>
              <span className="badge badge-leave-revoked">
                Revoked{dayInfo?.leaveType ? ` (${dayInfo.leaveType})` : ""}
              </span>
            </div>
          )}

          {(dayInfo?.dayType === "holiday" || dayInfo?.isCompanyHoliday) && (
            <div className="detail-row">
              <span className="detail-label">Holiday Reason</span>
              <span className="detail-value">
                {getHolidayReason(dayInfo.date)}
              </span>
            </div>
          )}

          {record && (
            <>
              <div className="time-grid">
                <div className="time-item">
                  <span className="time-label">Punch In</span>
                  <span className="time-value">
                    {formatTime(record.punchInTime)}
                  </span>
                </div>
                <div className="time-item">
                  <span className="time-label">Punch Out</span>
                  <span className="time-value">
                    {formatTime(record.punchOutTime)}
                  </span>
                </div>
              </div>

              {(record.punchInMessage || record.punchOutMessage) && (
                <div className="messages-section">
                  {record.punchInMessage && (
                    <div className="message-item">
                      <span className="message-label">In:</span>
                      <span className="message-text">
                        {record.punchInMessage}
                      </span>
                    </div>
                  )}
                  {record.punchOutMessage && (
                    <div className="message-item">
                      <span className="message-label">Out:</span>
                      <span className="message-text">
                        {record.punchOutMessage}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {canDelete && record?.attendanceId && (
          <div className="modal-footer">
            <button
              type="button"
              className="modal-button--delete"
              onClick={() => onDelete?.(record)}
            >
              <Trash2 size={14} />
              Delete Record
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Day Cell with Company Holiday Information
const EnhancedDayCell = ({
  record,
  dayInfo,
  onClick,
  expectedHours,
  workingDays,
  canDelete = false,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const getStatusColors = () => {
    // Days before the employee's date of joining aren't attendance days at
    // all — render them blank (like future dates) instead of "Absent".
    if (dayInfo?.dayType === "before-joining") {
      return {
        bg: "#ffffff",
        text: "#cbd5e1",
        border: "#f1f5f9",
        status: "",
      };
    }

    // Company holiday takes highest priority
    if (dayInfo?.isCompanyHoliday) {
      return {
        bg: "#fef3c7",
        text: "#92400e",
        border: "#fde68a",
        status: dayInfo.holidayName || "Holiday",
      };
    }

    // Working days holiday
    if (dayInfo?.dayType === "holiday") {
      return {
        bg: "#f1f5f9",
        text: "#64748b",
        border: "#cbd5e1",
        status: "Weekly Off",
      };
    }

    // Leave requested but not yet approved/rejected
    if (dayInfo?.dayType === "leave-requested") {
      return {
        bg: "#fef3c7",
        text: "#92400e",
        border: "#fcd34d",
        status: "Leave Requested",
      };
    }

    // Leave (approved)
    if (dayInfo?.dayType === "leave") {
      const isFullDay = isFullDayLeave(dayInfo.leaveDuration);
      return {
        bg: isFullDay ? "#fffbeb" : "#fefce8",
        text: isFullDay ? "#b45309" : "#ca8a04",
        border: isFullDay ? "#fde68a" : "#fef08a",
        status: isFullDay ? "LEAVE" : "½ DAY",
      };
    }

    // Date of joining — call this out explicitly rather than letting it
    // fall through to a plain Present/Not Punched label.
    if (dayInfo?.isJoiningDay) {
      return {
        bg: "#eef2ff",
        text: "#4338ca",
        border: "#c7d2fe",
        status: "Joined",
      };
    }

    // Future date
    const cellDate = new Date(dayInfo?.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cellDate.setHours(0, 0, 0, 0);

    if (cellDate > today) {
      return {
        bg: "#ffffff",
        text: "#94a3b8",
        border: "#f1f5f9",
        status: "",
      };
    }

    // Attendance status
    if (!record) {
      return {
        bg: "#fef2f2",
        text: "#dc2626",
        border: "#fecaca",
        status: "Not Punched",
      };
    }

    if (record.punchInTime && record.punchOutTime) {
      return {
        bg: "#f0fdf4",
        text: "#16a34a",
        border: "#bbf7d0",
        status: "Present",
      };
    }

    if (record.punchInTime && !record.punchOutTime) {
      return {
        bg: "#fffbeb",
        text: "#d97706",
        border: "#fde68a",
        status: "Partial",
      };
    }

    return {
      bg: "#fef2f2",
      text: "#dc2626",
      border: "#fecaca",
      status: "Not Punched",
    };
  };

  const calculateHours = () => {
    if (!record || !record.punchInTime || !record.punchOutTime) return null;

    try {
      const parseTime = (timeString) => {
        if (!timeString || timeString === "—") return 0;

        // Handle "08:00 am" format
        const timeRegex = /(\d{1,2}):(\d{2})\s*(am|pm)/i;
        const match = timeString.match(timeRegex);
        if (match) {
          let hour = parseInt(match[1]);
          const minute = parseInt(match[2]);
          const period = match[3].toLowerCase();
          if (period === "pm" && hour !== 12) hour += 12;
          if (period === "am" && hour === 12) hour = 0;
          return hour * 60 + minute;
        }

        // Handle "01:19:00" format (HH:MM:SS)
        if (timeString.includes(":")) {
          const parts = timeString.split(":");
          if (parts.length >= 2) {
            const hour = parseInt(parts[0], 10);
            const minute = parseInt(parts[1], 10);
            return hour * 60 + minute;
          }
        }
        return 0;
      };

      const punchInMinutes = parseTime(record.punchInTime);
      const punchOutMinutes = parseTime(record.punchOutTime);

      if (punchInMinutes === 0 || punchOutMinutes === 0) return null;

      let diffMinutes = punchOutMinutes - punchInMinutes;
      if (diffMinutes < 0) diffMinutes += 24 * 60;
      if (diffMinutes < 1) return null;

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      return { hours, minutes };
    } catch (e) {
      console.error("Error calculating hours:", e);
      return null;
    }
  };

  const formatTimeDisplay = (timeString) => {
    if (!timeString || timeString === "—") return "—";

    // Handle "08:00 am" format
    const timeRegex = /(\d{1,2}):(\d{2})\s*(am|pm)/i;
    const match = timeString.match(timeRegex);
    if (match) {
      let hour = parseInt(match[1]);
      const minute = parseInt(match[2]);
      const period = match[3].toUpperCase();
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

      if (minute === 0) {
        return `${displayHour} ${period}`;
      } else {
        return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
      }
    }

    // Handle "01:19:00" format
    if (timeString.includes(":") && timeString.split(":").length === 3) {
      const parts = timeString.split(":");
      const hour = parseInt(parts[0], 10);
      const minute = parseInt(parts[1], 10);
      const period = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;

      if (minute === 0) {
        return `${hour12} ${period}`;
      } else {
        return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
      }
    }

    return timeString;
  };

  const statusColors = getStatusColors();
  const hours = calculateHours();
  const isClickable =
    record || (dayInfo?.dayType !== "future" && statusColors.status !== "");
  const hasValidTimeRecord =
    record && (record.punchInTime || record.punchOutTime);
  const showTimeSection = hasValidTimeRecord;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: isClickable ? "pointer" : "default",
        backgroundColor: statusColors.bg,
        borderRadius: "8px",
        border: `2px solid ${statusColors.border}`,
        transition: "all 0.2s ease",
        minHeight: "120px",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          fontSize: "14px",
          fontWeight: 700,
          color: "#374151",
          zIndex: 1,
        }}
      >
        {dayInfo?.date?.split("-")[2]}
      </div>

      {canDelete && isHovered && record?.attendanceId && (
        <button
          type="button"
          className="day-cell-delete-btn"
          title="Delete attendance record"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(record);
          }}
        >
          <Trash2 size={10} />
        </button>
      )}

      {(dayInfo?.dayType === "leave" ||
        dayInfo?.dayType === "leave-requested") &&
        showTimeSection && (
          <div
            style={{
              color: statusColors.text,
              background: statusColors.bg,
              border: `1px solid ${statusColors.border}`,
              fontSize: "11px",
              fontWeight: 700,
              textAlign: "center",
              padding: "2px 8px",
              borderRadius: "6px",
              marginBottom: "4px",
            }}
          >
            {statusColors.status}
          </div>
        )}

      {dayInfo?.leaveRevoked && (
        <div
          style={{
            color: "#b91c1c",
            background: "#fee2e2",
            fontSize: "10px",
            fontWeight: 700,
            textAlign: "center",
            padding: "2px 8px",
            borderRadius: "6px",
            marginBottom: "4px",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          Leave Revoked
        </div>
      )}

      {showTimeSection && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            width: "100%",
          }}
        >
          {record.punchInTime && record.punchInTime !== "—" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#1f2937",
                  letterSpacing: "0.5px",
                }}
              >
                {formatTimeDisplay(record.punchInTime)}
              </div>
            </div>
          )}

          {record.punchOutTime && record.punchOutTime !== "—" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: "#6b7280",
                  marginBottom: "2px",
                }}
              >
                TO
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#1f2937",
                  letterSpacing: "0.5px",
                }}
              >
                {formatTimeDisplay(record.punchOutTime)}
              </div>
            </div>
          )}

          {hours && record.punchInTime && record.punchOutTime && (
            <div
              style={{
                padding: "6px 10px",
                backgroundColor: "#f3f4f6",
                borderRadius: "6px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1,
                }}
              >
                {hours.hours}
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6b7280",
                  }}
                >
                  h
                </span>
                {hours.minutes > 0 && (
                  <>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      {hours.minutes}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      m
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!showTimeSection && statusColors.status && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            width: "100%",
            gap: "4px",
          }}
        >
          <div
            style={{
              color: statusColors.text,
              fontSize: "13px",
              fontWeight: 700,
              textAlign: "center",
              padding: "0 8px",
            }}
          >
            {statusColors.status}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Enhanced Reusable Calendar Component
function EnhancedReusableCalendar({
  apiEndpoint = `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/employee-monthly`,
  expectedHoursPerDay = 9,
  expectedDaysPerWeek = 6,
  showEmployeeSelector = false,
  employeeId = null,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDayRecord, setSelectedDayRecord] = useState(null);
  const [selectedDayInfo, setSelectedDayInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const canDeleteAttendance =
    user?.authorities?.includes("ROLE_ADMIN") ||
    user?.authorities?.includes("ROLE_HR");

  const [pendingDeleteRecord, setPendingDeleteRecord] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const handleDeleteAttendance = (record) => {
    if (!record?.attendanceId) return;

    if (!canDeleteAttendance) {
      toast.error("You do not have permission to delete attendance records");
      return;
    }

    setPendingDeleteRecord(record);
    setShowDeleteWarning(true);
  };

  const cancelDeleteAttendance = () => {
    setShowDeleteWarning(false);
    setPendingDeleteRecord(null);
  };

  const confirmDeleteAttendance = async () => {
    const attendanceId = pendingDeleteRecord?.attendanceId;
    if (!attendanceId) return;

    setShowDeleteWarning(false);

    const isAdmin = user?.authorities?.includes("ROLE_ADMIN");

    // Admin and HR hit different base paths (/api/admin vs /api/hr);
    // admin takes priority when a user holds both roles.
    const endpoint = isAdmin
      ? `${process.env.REACT_APP_API_BASE_URL}/attendance/${attendanceId}`
      : `${process.env.REACT_APP_API_HR_URL}/attendance/${attendanceId}`;

    try {
      const response = await axiosInstance.delete(endpoint);

      if (response.data.status === 200) {
        // This component owns its own fetched data (separate from the
        // parent's calendarData), so update it locally instead of relying
        // on the parent to refetch.
        setData((prev) =>
          Array.isArray(prev)
            ? prev.map((employee) => ({
                ...employee,
                monthlyAttendance: (employee.monthlyAttendance || []).filter(
                  (item) => item.attendanceId !== attendanceId,
                ),
              }))
            : prev,
        );
        toast.success("Attendance record deleted successfully");
        setSelectedDayRecord(null);
        setSelectedDayInfo(null);
      } else {
        toast.error("Failed to delete attendance record");
      }
    } catch (deleteError) {
      const status = deleteError?.response?.status;
      const message =
        deleteError?.response?.data?.message ||
        deleteError?.response?.data?.error ||
        "Error deleting attendance record";

      if (status === 403) {
        toast.error("You are not authorized to delete this record");
      } else if (status === 404) {
        toast.error("Attendance record not found");
      } else {
        toast.error(message);
      }
      console.error("Error deleting attendance:", deleteError);
    } finally {
      setPendingDeleteRecord(null);
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { daysInMonth, year, month };
  };

  const { daysInMonth, year, month } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const getAttendanceMap = useMemo(() => {
    if (!Array.isArray(data)) return {};
    const map = {};
    data.forEach((employee) => {
      if (
        employee.monthlyAttendance &&
        Array.isArray(employee.monthlyAttendance)
      ) {
        employee.monthlyAttendance.forEach((record) => {
          const key = `${employee.employeeId}-${record.attendanceDate}`;
          map[key] = {
            ...record,
            employeeName: employee.employeeName,
            employeeId: employee.employeeId,
          };
        });
      }
    });
    return map;
  }, [data]);

  const getLeaveMap = useMemo(() => {
    if (!Array.isArray(data)) return {};
    const map = {};
    data.forEach((employee) => {
      if (employee.leaveHistories && Array.isArray(employee.leaveHistories)) {
        employee.leaveHistories.forEach((leave) => {
          const key = `${employee.employeeId}-${leave.leaveDate}`;
          map[key] = {
            ...leave,
            employeeName: employee.employeeName,
            employeeId: employee.employeeId,
          };
        });
      }
    });
    return map;
  }, [data]);

  // Get holidays from employee data
  const holidays = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    // Use holidays from the first employee (or the specific employee if viewing single)
    const employee =
      data.find((emp) => emp.employeeId === parseInt(employeeId)) || data[0];
    return employee?.holidays || [];
  }, [data, employeeId]);

  const attendanceMap = getAttendanceMap;
  const leaveMap = getLeaveMap;

  const workingDays = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return data[0].workingDays;
  }, [data]);

  const isHoliday = useMemo(
    () => (date) => {
      if (!workingDays) return false;
      const dayOfWeek = date.getDay();
      const dayName = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ][dayOfWeek];

      if (!workingDays[dayName]) return true;

      const weekOfMonth = Math.ceil(date.getDate() / 7);
      const weekNames = ["first", "second", "third", "fourth", "fifth"];
      const weekName = weekNames[weekOfMonth - 1];

      if (
        dayName === "saturday" &&
        workingDays[
          `leaveOn${
            weekName.charAt(0).toUpperCase() + weekName.slice(1)
          }Saturday`
        ]
      ) {
        return true;
      }
      if (
        dayName === "sunday" &&
        workingDays[
          `leaveOn${weekName.charAt(0).toUpperCase() + weekName.slice(1)}Sunday`
        ]
      ) {
        return true;
      }

      return false;
    },
    [workingDays],
  );

  const isCompanyHoliday = (dateString) => {
    return holidays.find((holiday) => holiday.eventDate === dateString);
  };

  const stats = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        totalPresent: 0,
        totalAbsent: 0,
        totalPartial: 0,
        totalHoliday: 0,
        totalLeave: 0,
        totalCompanyHolidays: 0,
        totalWorkingDays: 0,
        totalDaysWorked: 0,
        totalHoursWorked: "0h 0m",
        avgHoursPerDay: "0h",
        attendanceRate: 0,
      };
    }

    const employee = employeeId
      ? data.find((emp) => emp.employeeId === Number(employeeId))
      : data[0];

    if (!employee) {
      return {
        totalPresent: 0,
        totalAbsent: 0,
        totalPartial: 0,
        totalHoliday: 0,
        totalLeave: 0,
        totalCompanyHolidays: 0,
        totalWorkingDays: 0,
        totalDaysWorked: 0,
        totalHoursWorked: "0h 0m",
        avgHoursPerDay: "0h",
        attendanceRate: 0,
      };
    }
    const attendanceMap = {};
    const leaveMap = {};

    employee.monthlyAttendance?.forEach((a) => {
      attendanceMap[a.attendanceDate] = a;
    });

    employee.leaveHistories?.forEach((l) => {
      leaveMap[l.leaveDate] = l;
    });

    // Days before the employee joined aren't attendance days — exclude
    // them from every stat rather than letting them count as absent.
    const dojKey = employee.doj ? employee.doj.substring(0, 10) : null;

    let totalPresent = 0;
    let totalAbsent = 0;
    let totalPartial = 0;
    let totalHoliday = 0;
    let totalLeave = 0;
    let totalCompanyHolidays = 0;
    let totalWorkingDays = 0;
    let totalDaysWorked = 0;
    let totalHoursWorked = 0;

    const today = new Date();
    const daysToCount =
      today.getFullYear() === year && today.getMonth() === month
        ? today.getDate()
        : daysInMonth;

    const toMinutes = (t) => {
      if (!t) return 0;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };

    for (let day = 1; day <= daysToCount; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const date = new Date(year, month, day);

      if (date > today) continue;
      if (dojKey && dateKey < dojKey) continue;

      const record = attendanceMap[dateKey];
      // A revoked leave no longer counts as leave — treat the day as a
      // normal working day for stats purposes.
      const leave = getEffectiveLeave(leaveMap[dateKey]);

      const companyHoliday = isCompanyHoliday(dateKey);
      if (companyHoliday) {
        const isOptional = companyHoliday.holidayType === "Optional";
        if (isOptional && leave) {
          // Optional holiday but employee took leave — count as leave
          totalLeave++;
        } else {
          totalCompanyHolidays++;
        }
        continue;
      }

      if (isHoliday(date)) {
        totalHoliday++;
        continue;
      }

      // Full-day leave
      if (leave && isFullDayLeave(leave.leaveDuration)) {
        totalLeave++;
        continue;
      }

      totalWorkingDays++;

      if (record?.punchInTime && record?.punchOutTime) {
        totalPresent++;
        totalDaysWorked++;

        let diff =
          toMinutes(record.punchOutTime) - toMinutes(record.punchInTime);

        // Punch-out earlier than punch-in on the same record means the
        // punch-out rolled into the next day (these go through an approval
        // workflow before landing here, so treat them as legitimate rather
        // than discarding the hours).
        if (diff < 0) diff += 24 * 60;

        if (diff > 0) totalHoursWorked += diff / 60;
      } else if (record?.punchInTime && !record?.punchOutTime) {
        totalPartial++;
      } else {
        totalAbsent++;
      }
    }

    return {
      totalPresent,
      totalAbsent,
      totalPartial,
      totalHoliday,
      totalLeave,
      totalCompanyHolidays,
      totalWorkingDays,
      totalDaysWorked,
      totalHoursWorked: `${Math.floor(totalHoursWorked)}h ${Math.round(
        (totalHoursWorked % 1) * 60,
      )}m`,
      avgHoursPerDay:
        totalDaysWorked > 0
          ? (totalHoursWorked / totalDaysWorked).toFixed(1) + "h"
          : "0h",
      attendanceRate:
        totalWorkingDays > 0
          ? Math.round((totalPresent / totalWorkingDays) * 100)
          : 0,
    };
  }, [data, employeeId, year, month, daysInMonth, isHoliday]);

  const requestIdRef = React.useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const formattedDate = `${currentMonth.getFullYear()}-${String(
          currentMonth.getMonth() + 1,
        ).padStart(2, "0")}-01`;

        // employeeId is a PATH parameter on this endpoint
        // (/attendance/employee-monthly/{employeeId}), not a query param.
        // Sending it as ?employeeId=... never hits the per-employee route,
        // which is why this always returned every employee regardless of
        // the employeeId prop.
        const url = employeeId ? `${apiEndpoint}/${employeeId}` : apiEndpoint;

        const response = await axiosInstance.get(url, {
          params: {
            attendanceDate: formattedDate,
          },
        });

        if (requestId !== requestIdRef.current) return;

        if (response.data.status === 200) {
          let dataToSet;

          if (Array.isArray(response.data.data)) {
            dataToSet = response.data.data;
          } else if (
            response.data.data &&
            typeof response.data.data === "object"
          ) {
            dataToSet = [response.data.data];
          } else {
            dataToSet = [];
          }

          setData(dataToSet);
        } else {
          throw new Error(
            response.data.message || "Failed to fetch attendance",
          );
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err.message || "Failed to fetch attendance");
        console.error("Error fetching attendance data:", err);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    };

    fetchData();
  }, [currentMonth, apiEndpoint, employeeId]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          style={styles.retryButton}
        >
          Retry
        </button>
      </div>
    );
  }

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const firstDay = new Date(year, month, 1).getDay();

  const employeeName =
    employeeId && Array.isArray(data) && data.length === 1
      ? data[0].employeeName
      : "Team Calendar";

  return (
    <div style={styles.container}>
      <style>{modalStyles}</style>

      <div style={styles.dashboardLayout}>
        <div style={styles.calendarSection}>
          <div style={styles.calendarWrapper}>
            <div style={styles.weekdays}>
              {[
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((day) => (
                <div key={day} style={styles.weekday}>
                  {day}
                </div>
              ))}
            </div>

            <div style={styles.grid}>
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`empty-${i}`} style={styles.emptyCell} />
              ))}

              {days.map((day) => {
                const dateKey = `${year}-${String(month + 1).padStart(
                  2,
                  "0",
                )}-${String(day).padStart(2, "0")}`;
                const date = new Date(year, month, day);

                const employee = Array.isArray(data)
                  ? data.find(
                      (emp) => emp.employeeId === parseInt(employeeId),
                    ) || data[0]
                  : data;

                // Add null check to prevent error
                const record = employee
                  ? attendanceMap[`${employee.employeeId}-${dateKey}`]
                  : null;
                const rawLeave = employee
                  ? leaveMap[`${employee.employeeId}-${dateKey}`]
                  : null;
                const leaveRevoked =
                  rawLeave && isRevokedLeave(rawLeave.leaveStatus);
                const leavePending =
                  rawLeave &&
                  !leaveRevoked &&
                  isPendingLeave(rawLeave.leaveStatus);
                // A revoked leave no longer blocks the day.
                const leave = leaveRevoked ? null : rawLeave;
                const isHol = isHoliday(date);
                const companyHoliday = isCompanyHoliday(dateKey);

                const isOptionalHoliday =
                  companyHoliday && companyHoliday.holidayType === "Optional";

                // DOJ comes back as a plain "YYYY-MM-DD" date, same shape as
                // dateKey, so it can be compared directly as a string.
                const employeeDoj = employee?.doj
                  ? employee.doj.substring(0, 10)
                  : null;
                const isBeforeJoining = employeeDoj
                  ? dateKey < employeeDoj
                  : false;
                const isJoiningDay = employeeDoj
                  ? dateKey === employeeDoj
                  : false;

                const dayInfo = isBeforeJoining
                  ? {
                      date: dateKey,
                      employeeName: employee?.employeeName || "",
                      dayType: "before-joining",
                    }
                  : companyHoliday && !(isOptionalHoliday && leave)
                    ? {
                        date: dateKey,
                        employeeName: employee?.employeeName || "",
                        dayType: "company-holiday",
                        isCompanyHoliday: true,
                        holidayName: companyHoliday.holidayName,
                        holidayType: companyHoliday.holidayType,
                        leaveRevoked,
                        isJoiningDay,
                      }
                    : {
                        date: dateKey,
                        employeeName: employee?.employeeName || "",
                        dayType: isHol
                          ? "holiday"
                          : leave
                            ? leavePending
                              ? "leave-requested"
                              : "leave"
                            : "working",
                        leaveType: leave?.leaveTypeName,
                        leaveDuration: leave?.leaveDuration,
                        leaveStatus: leave?.leaveStatus,
                        leaveRevoked,
                        isJoiningDay,
                      };

                return (
                  <div key={day} style={styles.cell}>
                    <EnhancedDayCell
                      record={record}
                      dayInfo={dayInfo}
                      expectedHours={expectedHoursPerDay}
                      workingDays={workingDays}
                      canDelete={canDeleteAttendance}
                      onDelete={handleDeleteAttendance}
                      onClick={() => {
                        setSelectedDayRecord(record);
                        setSelectedDayInfo(dayInfo);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={styles.sidebarSection}>
          <div style={styles.header}>
            <div style={styles.monthNav}>
              <button onClick={() => changeMonth(-1)} style={styles.navBtn}>
                <ChevronLeft size={24} />
              </button>
              <h2 style={styles.monthTitle}>{monthName}</h2>
              <button onClick={() => changeMonth(1)} style={styles.navBtn}>
                <ChevronRight size={24} />
              </button>
            </div>
          </div>

          <div style={styles.bigQuickStats}>
            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#dcfce7" }}
              >
                <CheckCircle size={20} color="#16a34a" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={styles.bigQuickStatValue}>
                  {stats?.totalPresent ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Present Days</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#fee2e2" }}
              >
                <TrendingDown size={20} color="#dc2626" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={{ ...styles.bigQuickStatValue, color: "#dc2626" }}>
                  {stats.totalAbsent}
                </div>
                <div style={styles.bigQuickStatLabel}>Not Punched Days</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#dbeafe" }}
              >
                <Calendar size={20} color="#2563eb" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={{ ...styles.bigQuickStatValue, color: "#2563eb" }}>
                  {stats?.totalHoliday ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Weekly Offs</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#fef3c7" }}
              >
                <span style={{ fontSize: "20px" }}>🎉</span>
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={{ ...styles.bigQuickStatValue, color: "#d97706" }}>
                  {stats?.totalCompanyHolidays ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Holidays</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#ffedd5" }}
              >
                <AlertCircle size={20} color="#ea580c" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={{ ...styles.bigQuickStatValue, color: "#ea580c" }}>
                  {stats?.totalLeave ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Leave Days</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#f3f4f6" }}
              >
                <Clock size={20} color="#374151" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={styles.bigQuickStatValue}>
                  {stats?.totalHoursWorked ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Total Hours</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#ecfdf5" }}
              >
                <Target size={20} color="#10b981" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={styles.bigQuickStatValue}>
                  {stats?.avgHoursPerDay ?? 0}
                </div>
                <div style={styles.bigQuickStatLabel}>Avg Hours/Day</div>
              </div>
            </div>

            <div style={styles.bigQuickStat}>
              <div
                style={{ ...styles.bigQuickStatIcon, background: "#e0e7ff" }}
              >
                <TrendingUp size={20} color="#4f46e5" />
              </div>
              <div style={styles.bigQuickStatContent}>
                <div style={{ ...styles.bigQuickStatValue, color: "#4f46e5" }}>
                  {stats?.attendanceRate ?? 0}%
                </div>
                <div style={styles.bigQuickStatLabel}>Attendance Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(selectedDayRecord || selectedDayInfo) && (
        <DayDetailsModal
          record={selectedDayRecord}
          dayInfo={selectedDayInfo}
          workingDays={workingDays}
          canDelete={canDeleteAttendance}
          onDelete={handleDeleteAttendance}
          onClose={() => {
            setSelectedDayRecord(null);
            setSelectedDayInfo(null);
          }}
        />
      )}

      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteAttendance}
        onDecline={cancelDeleteAttendance}
        onAccept={confirmDeleteAttendance}
        title="Delete Attendance Record"
        message="Are you sure you want to delete this attendance record? This action cannot be undone."
        acceptText="Delete"
        declineText="Cancel"
      />
    </div>
  );
}

const styles = {
  container: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    maxWidth: "100%",
    overflow: "hidden",
  },
  dashboardLayout: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    "@media (max-width: 1024px)": {
      flexDirection: "column",
    },
  },
  calendarSection: {
    flex: 1,
    minWidth: 0,
  },
  sidebarSection: {
    width: "280px",
    "@media (max-width: 1024px)": {
      width: "100%",
    },
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
    color: "#6b7280",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    gap: "16px",
  },
  errorText: {
    color: "#ef4444",
    fontSize: "16px",
    fontWeight: 600,
    textAlign: "center",
  },
  retryButton: {
    padding: "10px 20px",
    background: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "14px",
  },
  header: {
    marginBottom: "24px",
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px",
    background: "#3b82f6",
    borderRadius: "12px",
    color: "#fff",
    marginBottom: "12px",
  },
  monthTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "0.5px",
    textAlign: "center",
    flex: 1,
  },
  navBtn: {
    padding: "10px",
    border: "none",
    background: "rgba(255, 255, 255, 0.2)",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    color: "#fff",
    transition: "all 0.2s",
  },
  calendarWrapper: {
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    overflow: "hidden",
    background: "#fff",
  },
  weekdays: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    background: "#3b82f6",
  },
  weekday: {
    padding: "10px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 700,
    color: "#fff",
    letterSpacing: "1px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
    background: "#e5e7eb",
  },
  cell: {
    background: "#fff",
    minHeight: "90px",
  },
  emptyCell: {
    background: "#f9fafb",
    minHeight: "120px",
  },
  bigQuickStats: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  bigQuickStat: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "5px",
    background: "#fff",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  },
  bigQuickStatIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  bigQuickStatContent: {
    flex: 1,
  },
  bigQuickStatValue: {
    fontSize: "28px",
    fontWeight: 800,
    color: "#1f2937",
    lineHeight: 1,
    marginBottom: "4px",
  },
  bigQuickStatLabel: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: 700,
    letterSpacing: "0.8px",
  },
};

const modalStyles = `
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 16px;
  }
  
  .modal-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    max-width: 480px;
    width: 100%;
    max-height: 85vh;
    overflow: auto;
  }
  
  .modal-header {
    padding: 16px 20px;
    margin-bottom: 0;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }
  
  .modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
  
  .modal-close {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #9ca3af;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s;
  }
  
  .modal-close:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  
  .detail-row:last-child {
    border-bottom: none;
  }
  
  .detail-label {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
  }
  
  .detail-value {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }
  
  .badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: white;
  }
  
  .badge-holiday {
    background: #94a3b8;
  }
  
  .badge-leave {
    background: #fb923c;
  }

  .badge-leave-requested {
    background: #f59e0b;
  }

  .badge-leave-revoked {
    background: #dc2626;
  }

  .badge-joined {
    background: #4338ca;
  }
  
  .badge-working {
    background: #10b981;
  }
  
  .badge-company-holiday {
    background: #f59e0b;
  }
  
  .time-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin: 16px 0;
    padding: 16px;
    background: #f9fafb;
    border-radius: 8px;
  }
  
  .time-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .time-label {
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
  }
  
  .time-value {
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
  }
  
  .messages-section {
    margin: 16px 0;
    padding: 12px;
    background: #fef3c7;
    border-radius: 8px;
    border-left: 3px solid #f59e0b;
  }
  
  .message-item {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  
  .message-item:last-child {
    margin-bottom: 0;
  }
  
  .message-label {
    font-size: 12px;
    font-weight: 700;
    color: #92400e;
    min-width: 30px;
  }
  
  .message-text {
    font-size: 13px;
    color: #78350f;
    flex: 1;
  }

  .modal-footer {
    padding: 12px 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
  }

  .modal-button--delete {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    background: #dc2626;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  .modal-button--delete:hover {
    background: #b91c1c;
  }

  .day-cell-delete-btn {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 18px;
    height: 18px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fee2e2;
    border: 1px solid #fecaca;
    border-radius: 4px;
    color: #dc2626;
    cursor: pointer;
    z-index: 2;
  }

  .day-cell-delete-btn:hover {
    background: #fecaca;
  }
`;

export default EnhancedReusableCalendar;
